import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FormContext, iControlProps } from "./../forms/types";
import Modal from "../modal/modal";
import { isMobile, isTablet, isDesktop } from "../theme/style-utils-coras-nua";

import { VideoAccess } from "./camera-detection-constants";
import CameraDetectionScreen from "./camera-detection-screens";
import init from "./camera-detection-init";
import AccessRequester from "./camera-detection-access-requerster";
import GetScreenName from "./camera-detection-get-screen-name";

type CameraDetectionProps = iControlProps & {
    context?: FormContext;
    deviceTypeList?: { [key: string]: boolean };
    onAccessGranted?: () => void;
    copyUrl?: string;
    copyUrlLabel?: string;
    allowsPrepopulate?: boolean;
    inputName?: string;
};

const CameraDetection = ({
    deviceTypeList,
    value,
    cssClass = "",
    onChange,
    onAccessGranted,
    copyUrl = "https://digital.bankofireland.com/PCA",
    copyUrlLabel = "digital.bankofireland.com/PCA",
    allowsPrepopulate = false,
    inputName = ""
}: CameraDetectionProps) => {
    const searchParams = useLocation().search;
    const [isLoading, setIsLoading] = useState(true);
    const [hasCamera, setHasCamera] = useState(false);
    const [videoAccess, setVideoAccess] = useState(VideoAccess.NotRequested);
    const [isOpen, setIsOpen] = useState(false);
    const changeVideoAccess = (v: VideoAccess) => {
        setVideoAccess(v);
        onChange && onChange(v);
    };

    const showScreen = useCallback(() => {
        for (const key in deviceTypeList) {
            /* istanbul ignore else */
            if (
                Object.prototype.hasOwnProperty.call(deviceTypeList, key) &&
                deviceTypeList[key] === true &&
                value !== VideoAccess.Granted
            ) {
                switch (key) {
                    // For desktops or devices with wider screen
                    case "desktop":
                        deviceTypeList[key] === isDesktop() &&
                            setIsOpen(isDesktop());
                        break;
                    // For mobiles or devices with thinner screen
                    case "mobile":
                        deviceTypeList[key] === (isMobile() || isTablet()) &&
                            setIsOpen(isMobile() || isTablet());
                        break;
                    default:
                        break;
                }
            }
        }
    }, [deviceTypeList, value]);

    if (allowsPrepopulate) {
        const urlSearchParams = new URLSearchParams(searchParams);
        const copySearchParams = new URLSearchParams();
        const queryParamNames = inputName.split("&");

        urlSearchParams.forEach((value, key) => {
            /* istanbul ignore else */
            if (queryParamNames.includes(key)) {
                copySearchParams.append(key, value);
            }
        });
        copyUrl += `?${copySearchParams.toString()}`;
    }

    useEffect(() => {
        init(setHasCamera, setIsLoading);
        showScreen();
    }, [showScreen]);

    return (
        <Modal
            isOpen={isOpen}
            className={`fullPage ${cssClass}`}
            overlayClassName="fullPage"
            modalName="cameraDetection"
        >
            <CameraDetectionScreen
                type={GetScreenName(isLoading, hasCamera, videoAccess)}
                onAccessGranted={() => {
                    onAccessGranted && onAccessGranted();
                    setIsOpen(false);
                }}
                setVideoAccess={changeVideoAccess}
                requestAccess={AccessRequester(changeVideoAccess, setIsLoading)}
                copyUrl={copyUrl}
                copyUrlLabel={copyUrlLabel}
            />
        </Modal>
    );
};

export default CameraDetection;
